import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@neo/primitives/base/select'
import { Flag } from '@neo/primitives/icon'
import { Input } from '@neo/primitives/input'
import { Tooltip } from '@neo/primitives/tooltip'
import { getCountryCodeFromIP } from '@shared/utils/country-detection'
import { Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { DataRegionNote } from 'v2/shared/components/data-region-note'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { nameSchema } from 'v2/shared/validation-schemas'
import type { RLoginResponse } from 'v2/types/api/auth'
import dayjs from 'v2/utils/dayjs'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import { useCompleteInviteSignupMutation, useValidateInviteSignupTokenQuery } from './queries'
import { validatePassword } from './utils'
import { useSessionManager } from './utils/session-utils'
import { redirectToTwoFa } from './utils/two-fa-utils'

interface InviteSignupFormData {
  first_name: string
  last_name: string
  password: string
}

const regionMap = {
  [REGIONS.IN]: { label: 'India', icon: <Flag iso="IN" size={16} circle /> },
  [REGIONS.US]: { label: 'United States', icon: <Flag iso="US" size={16} circle /> },
}

const trafficDestinationOptions = [
  {
    value: REGIONS.IN,
    label: regionMap[REGIONS.IN].label,
    icon: regionMap[REGIONS.IN].icon,
  },
  {
    value: REGIONS.US,
    label: regionMap[REGIONS.US].label,
    icon: regionMap[REGIONS.US].icon,
  },
]

const fields: GenericFieldConfig<InviteSignupFormData> = [
  {
    name: 'first_name',
    componentType: 'input',
    zodValidation: nameSchema('First name', 30),
    label: 'First Name',
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Enter your first name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'given-name',
      'data-testid': 'invite-signup-input-first-name',
    },
  },
  {
    name: 'last_name',
    componentType: 'input',
    zodValidation: nameSchema('Last name', 30),
    label: 'Last Name',
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Enter your last name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'family-name',
      'data-testid': 'invite-signup-input-last-name',
    },
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: (formState: InviteSignupFormData) =>
      validatePassword(formState.first_name, formState.last_name),
    label: 'Password',
    className: 'col-span-1 md:col-span-2',
    errorClassName: 'error-list-format',
    inputProps: {
      autoComplete: 'new-password',
      placeholder: 'Enter a secure password',
      inputClassName: AUTH_INPUT_CLASS,
      'data-testid': 'invite-signup-input-password',
    },
  },
]

const defaultFormValues: InviteSignupFormData = {
  first_name: '',
  last_name: '',
  password: '',
}

// Capture the token from the URL once, then strip it so it isn't persisted in browser
// history, sent in Referer, or picked up by analytics/error reporting on subsequent
// renders or navigations. The token remains in memory only for the life of this page.
const takeTokenFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  const token = url.searchParams.get('token')
  if (token) {
    url.searchParams.delete('token')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  }
  return token
}

export const InviteSignup = () => {
  const { userAuthenticated } = useAuth()
  const navigate = useNavigate()
  const tokenRef = useRef<string | null>(null)
  if (tokenRef.current === null) {
    tokenRef.current = takeTokenFromUrl()
  }
  const token = tokenRef.current
  const [selectedRegion, setSelectedRegion] = useState(REGIONS.US)
  const hasDetectedRef = useRef(false)

  const {
    data: tokenData,
    isLoading: isValidatingToken,
    isError: isTokenError,
  } = useValidateInviteSignupTokenQuery(token)

  const {
    mutate: completeInviteSignup,
    isPending: isSubmitting,
    error: submitError,
  } = useCompleteInviteSignupMutation()

  const { completeLoginProcess } = useSessionManager()

  useEffect(() => {
    const detectRegion = async () => {
      if (hasDetectedRef.current) return
      hasDetectedRef.current = true
      const countryCode = await getCountryCodeFromIP()
      setSelectedRegion(countryCode === 'IN' ? REGIONS.IN : REGIONS.US)
    }

    detectRegion()
  }, [])

  if (userAuthenticated) {
    return <Navigate to={getDefaultRoute()} />
  }

  if (!token || isTokenError) {
    return (
      <AuthLayout
        pageTitle="Sign up"
        title="Invite link invalid"
        description=""
        footer={
          <div className="text-p-400 text-secondary text-center">
            Already have an account?{' '}
            <Link to="/login" className={AUTH_LINK_CLASS} data-testid="invite-signup-link-login">
              Login
            </Link>
          </div>
        }
      >
        <Alert
          variant="destructive"
          content="This signup link is invalid or has expired."
          data-testid="invite-signup-invalid-token-alert"
        />
      </AuthLayout>
    )
  }

  if (isValidatingToken) {
    return (
      <AuthLayout pageTitle="Sign up" title="Sign up" description="">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted-foreground/30 border-t-primary" />
        </div>
      </AuthLayout>
    )
  }

  const email = tokenData?.data?.email ?? ''

  const handleSuccess = (loginResponse: RLoginResponse) => {
    if (loginResponse.two_fa_enabled) {
      redirectToTwoFa({ loginResponse, navigate, source: 'invite' })
    } else {
      completeLoginProcess(loginResponse)
      // Full reload so the app re-initializes with the freshly set auth cookies (matches invitations-v2).
      window.location.href = '/home'
    }
  }

  const onSubmit = (values: InviteSignupFormData) => {
    if (!token) return
    const tz = dayjs.tz.guess()
    completeInviteSignup(
      {
        token,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
        region: selectedRegion,
        timezone: { tzinfo: tz, title: tz },
      },
      {
        onSuccess: (response) => handleSuccess(response.data),
      },
    )
  }

  return (
    <AuthLayout
      pageTitle="Sign up"
      title="Complete your account"
      description="Fill in your details to get started."
    >
      {submitError && (
        <Alert
          variant="destructive"
          content={getErrorText(submitError)}
          data-testid="invite-signup-error-alert"
        />
      )}
      <div className="grid gap-2">
        <label htmlFor="invite-signup-email" className="text-p-400 text-secondary">
          Email
        </label>
        <Input
          inputClassName={AUTH_INPUT_CLASS}
          id="invite-signup-email"
          value={email}
          disabled
          autoComplete="username"
          data-testid="invite-signup-input-email"
        />
      </div>
      <GenericForm
        id="invite-signup-form"
        defaultValues={defaultFormValues}
        fields={fields}
        onSubmit={onSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        isSubmitting={isSubmitting}
        submitText="Create Account"
        includeSubmitButton={false}
      />
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center justify-center gap-1">
          <span className="text-p-400 text-secondary">Data Region </span>
          <Select value={selectedRegion} onValueChange={(value) => setSelectedRegion(value)}>
            <SelectTrigger
              className="max-w-40 border-none ring-0 shadow-none focus:ring-0"
              data-testid="invite-signup-select-region"
            >
              <div className="flex items-center gap-2">
                {regionMap[selectedRegion].icon}
                <span>{regionMap[selectedRegion].label}</span>
              </div>
            </SelectTrigger>
            <SelectContent position="popper">
              {trafficDestinationOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`invite-signup-select-region-${option.value}`}
                >
                  <span className="text-xs-600 text-primary flex items-center gap-2">
                    {option.icon} {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tooltip content="Please select the primary region where your data will be stored. This affects performance, compliance, and billing.">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>
        <DataRegionNote region={selectedRegion} canAddOtherRegion className="text-center" />
      </div>
      <FormSubmitButton
        formId="invite-signup-form"
        typography="action"
        className={AUTH_PRIMARY_BUTTON_CLASS}
        size="xl"
        loading={isSubmitting}
        data-testid="button-invite-signup-submit"
      >
        Create Account
      </FormSubmitButton>
      <div className="text-p-400 text-secondary text-center">
        Already have an account?{' '}
        <Link to="/login" className={AUTH_LINK_CLASS} data-testid="invite-signup-link-login">
          Login
        </Link>
      </div>
    </AuthLayout>
  )
}
